import { addMonths, format as formatDate, getDaysInMonth, getISODay, getWeek, isSameDay, subMonths } from 'date-fns';

export enum DAY_STATUS_ENUM {
    DEFAULT = 'DEFAULT',
    CURRENT = 'CURRENT',
    CURRENT_HOVER = 'CURRENT_HOVER',
    SELECTED = 'SELECTED',
    IN_RANGE = 'IN_RANGE',
    HOVER = 'HOVER'
}

export const getISOWeek = (date: number | Date) => getWeek(date, {
    weekStartsOn: 1,
    firstWeekContainsDate: 4
});

interface WeekInfo {
    no: number;
    year: number;
}

export function getWeeksStringFromDatesList(dates: Array<Date> | null): string {
    let result = '';

    if (Array.isArray(dates) && dates.length) {
        const weeks: Array<WeekInfo> = [];

        for (const date of dates) {
            const no = getISOWeek(date);
            let year = date.getFullYear();

            if (no >= 52 && date.getMonth() === 0) {
                year -= 1;
            }

            if (!weeks.find(w => w.no === no && w.year === year)) {
                weeks.push({
                    no,
                    year
                });
            }
        }

        weeks.sort((a, b) => {
            if (a.year === b.year) {
                return a.no - b.no;
            } else {
                return a.year - b.year;
            }
        });

        const currentYear = new Date().getFullYear();

        for (const week of weeks) {
            if (result.length) {
                result += ', ';
            }

            result += week.no;

            if (week.year !== currentYear) {
                result += `(${ week.year })`;
            }
        }
    }

    return result;
}


function padDay(dayNumber: number): string {
    const dayStr = String(dayNumber);

    if (dayStr.length < 2) {
        return `0${ dayStr }`;
    }

    return dayStr;
}

function padMonthLeft(baseDate: Date, monthStartWeekday: number): Date[] {
    const prevMonthDate = subMonths(baseDate, 1);
    const prevMonthString = formatDate(prevMonthDate, 'yyyy-MM');
    const daysInPrevMonth = getDaysInMonth(prevMonthDate);
    const days: Date[] = [];

    for (let i = 0; i < monthStartWeekday - 1; i++) {
        const day = new Date(`${ prevMonthString }-${ daysInPrevMonth - i }`);
        days.unshift(day);
    }

    return days;
}

function padMonthRight(baseDate: Date, monthFinishWeekday: number): Date[] {
    const nextMonthDate = addMonths(baseDate, 1);
    const nextMonthString = formatDate(nextMonthDate, 'yyyy-MM');
    const days: Date[] = [];

    for (let i = 1; i <= 7 - monthFinishWeekday; i++) {
        const day = new Date(`${ nextMonthString }-${ padDay(i) }`);
        days.push(day);
    }

    return days;
}

export function getDays(baseDate: Date): Date[] {
    let days: Date[] = [];
    const daysInMonth = getDaysInMonth(baseDate);

    for (let i = 1; i <= daysInMonth; i++) {
        const baseMonthString = formatDate(baseDate, 'yyyy-MM');
        const day = new Date(`${ baseMonthString }-${ padDay(i) }`);

        day.setHours(0, 0, 0, 0);

        days.push(day);
    }

    const firstDayString = `${ formatDate(baseDate, 'yyyy-MM') }-01`;
    const baseMonthStartWeekday = getISODay(new Date(firstDayString));

    // check if we need to prepend collection with previous month's days
    if (baseMonthStartWeekday > 1) {
        const daysToPrepend = padMonthLeft(baseDate, baseMonthStartWeekday);
        days = daysToPrepend.concat(days);
    }

    const lastDayString = `${ formatDate(baseDate, 'yyyy-MM') }-${ daysInMonth }`;
    const baseMonthFinishWeekday = getISODay(new Date(lastDayString));

    // check if we need to append collection with next month's days
    if (baseMonthFinishWeekday < 7) {
        const daysToAppend = padMonthRight(baseDate, baseMonthFinishWeekday);
        days = days.concat(daysToAppend);
    }

    return days;
}

export function getWeeks(days: Date[]): Date[][] {
    const mutableDays = [ ...days ];
    const weeks: Date[][] = [];

    if (mutableDays.length < 7) {
        return [ days ];
    }

    while (mutableDays.length > 7) {
        weeks.push(mutableDays.splice(0, 7));
    }

    if (mutableDays.length > 0) {
        weeks.push(mutableDays);
    }

    return weeks;
}

export const checkHoliday = (day: Date, holidays?: Array<Date | string>): boolean => {
    if (Array.isArray(holidays)) {
        return holidays.findIndex((holiday) => isSameDay(new Date(holiday), day)) >= 0;
    }

    return false;
};

export const isTouchDevice = () => (('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) ||
    (navigator.msMaxTouchPoints > 0));
